package com.example.event_management_system.ui.screens.calendar

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IEventRepository
import com.example.event_management_system.data.repository.ILocationRepository
import com.example.event_management_system.data.repository.IUserRepository
import com.example.event_management_system.util.currentLocalDate
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import kotlinx.datetime.plus

enum class CalendarViewMode { DAY, WEEK, MONTH, YEAR }

data class CalendarState(
    val currentMonth: LocalDate = LocalDate(1970, 1, 1),
    val selectedDate: LocalDate? = null,
    val events: List<Event> = emptyList(),
    val locations: List<Location> = emptyList(),
    val selectedLocationId: String? = null,
    val currentUser: User? = null,
    val isLoading: Boolean = true,
    val error: String? = null,
    val viewMode: CalendarViewMode = CalendarViewMode.MONTH,
    val searchQuery: String = "",
    val isSignedOut: Boolean = false
)

class CalendarScreenModel(
    private val eventRepository: IEventRepository,
    private val locationRepository: ILocationRepository,
    private val userRepository: IUserRepository,
    private val authRepository: IAuthRepository
) : ScreenModel {

    private val _state = MutableStateFlow(CalendarState())
    val state: StateFlow<CalendarState> = _state.asStateFlow()

    init {
        try {
            _state.update { it.copy(currentMonth = currentLocalDate()) }
        } catch (_: Throwable) {}
        loadUserAndLocations()
    }

    private fun loadUserAndLocations() {
        screenModelScope.launch {
            try {
                _state.update { it.copy(isLoading = true) }

                val userId = authRepository.getCurrentUserId()
                if (userId == null) {
                    _state.update { it.copy(isLoading = false, error = "Not authenticated") }
                    return@launch
                }

                val user = userRepository.getUser(userId)
                if (user == null) {
                    _state.update { it.copy(isLoading = false, error = "User not found") }
                    return@launch
                }

                _state.update { it.copy(currentUser = user) }

                // Load locations based on user role
                launch {
                    try {
                        val locationsFlow = if (user.role == UserRole.OWNER) {
                            locationRepository.getAllLocationsFlow()
                        } else {
                            locationRepository.getLocationsByIdsFlow(user.locationIds)
                        }

                        locationsFlow.collect { locations ->
                            _state.update { currentState ->
                                val selectedId = currentState.selectedLocationId
                                    ?: locations.firstOrNull()?.id
                                currentState.copy(
                                    locations = locations,
                                    selectedLocationId = selectedId
                                )
                            }
                            loadEventsForCurrentMonth()
                        }
                    } catch (e: Throwable) {
                        _state.update { it.copy(isLoading = false, error = e.message ?: "Failed to load locations") }
                    }
                }
            } catch (e: Throwable) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "Failed to load data") }
            }
        }
    }

    private fun loadEventsForCurrentMonth() {
        val currentState = _state.value
        val locationId = currentState.selectedLocationId ?: run {
            _state.update { it.copy(isLoading = false) }
            return
        }
        val currentMonth = currentState.currentMonth

        screenModelScope.launch {
            try {
                val startOfMonth = LocalDate(currentMonth.year, currentMonth.month, 1)
                    .atStartOfDayIn(TimeZone.UTC)
                    .toEpochMilliseconds()

                val nextMonth = currentMonth.plus(1, DateTimeUnit.MONTH)
                val endOfMonth = LocalDate(nextMonth.year, nextMonth.month, 1)
                    .atStartOfDayIn(TimeZone.UTC)
                    .toEpochMilliseconds()

                eventRepository.getEventsByMonthFlow(locationId, startOfMonth, endOfMonth)
                    .collect { events ->
                        _state.update {
                            it.copy(events = events, isLoading = false, error = null)
                        }
                        autoCompletePassedEvents(events)
                    }
            } catch (e: Throwable) {
                _state.update { it.copy(isLoading = false, error = e.message ?: "Failed to load events") }
            }
        }
    }

    fun previousMonth() {
        _state.update { currentState ->
            val newMonth = currentState.currentMonth.plus(-1, DateTimeUnit.MONTH)
            currentState.copy(currentMonth = newMonth)
        }
        loadEventsForCurrentMonth()
    }

    fun nextMonth() {
        _state.update { currentState ->
            val newMonth = currentState.currentMonth.plus(1, DateTimeUnit.MONTH)
            currentState.copy(currentMonth = newMonth)
        }
        loadEventsForCurrentMonth()
    }

    fun selectLocation(locationId: String) {
        _state.update { it.copy(selectedLocationId = locationId) }
        loadEventsForCurrentMonth()
    }

    fun refresh() {
        loadEventsForCurrentMonth()
    }

    fun goToToday() {
        _state.update { it.copy(currentMonth = currentLocalDate()) }
        loadEventsForCurrentMonth()
    }

    fun previousPeriod() {
        _state.update {
            val newDate = when (it.viewMode) {
                CalendarViewMode.DAY  -> it.currentMonth.plus(-1, DateTimeUnit.DAY)
                CalendarViewMode.WEEK -> it.currentMonth.plus(-7, DateTimeUnit.DAY)
                CalendarViewMode.MONTH -> it.currentMonth.plus(-1, DateTimeUnit.MONTH)
                CalendarViewMode.YEAR -> LocalDate(it.currentMonth.year - 1, it.currentMonth.month, 1)
            }
            it.copy(currentMonth = newDate)
        }
        loadEventsForCurrentMonth()
    }

    fun nextPeriod() {
        _state.update {
            val newDate = when (it.viewMode) {
                CalendarViewMode.DAY  -> it.currentMonth.plus(1, DateTimeUnit.DAY)
                CalendarViewMode.WEEK -> it.currentMonth.plus(7, DateTimeUnit.DAY)
                CalendarViewMode.MONTH -> it.currentMonth.plus(1, DateTimeUnit.MONTH)
                CalendarViewMode.YEAR -> LocalDate(it.currentMonth.year + 1, it.currentMonth.month, 1)
            }
            it.copy(currentMonth = newDate)
        }
        loadEventsForCurrentMonth()
    }

    fun navigateToMonth(date: LocalDate) {
        _state.update { it.copy(currentMonth = date, viewMode = CalendarViewMode.MONTH) }
        loadEventsForCurrentMonth()
    }

    fun selectDay(date: LocalDate) {
        _state.update { it.copy(selectedDate = date, currentMonth = date) }
        loadEventsForCurrentMonth()
    }

    fun setViewMode(mode: CalendarViewMode) {
        _state.update {
            it.copy(
                viewMode = mode,
                selectedDate = null,
                currentMonth = currentLocalDate()
            )
        }
        loadEventsForCurrentMonth()
    }

    fun setSearchQuery(query: String) {
        _state.update { it.copy(searchQuery = query) }
    }

    private fun autoCompletePassedEvents(events: List<Event>) {
        val todayStartMs = currentLocalDate()
            .atStartOfDayIn(TimeZone.UTC)
            .toEpochMilliseconds()
        val toComplete = events.filter {
            it.eventDate < todayStartMs &&
            (it.status == EventStatus.PENDING || it.status == EventStatus.CONFIRMED)
        }
        if (toComplete.isEmpty()) return
        screenModelScope.launch {
            toComplete.forEach { event ->
                try {
                    eventRepository.updateEvent(event.id, event.copy(status = EventStatus.COMPLETED))
                } catch (_: Throwable) {}
            }
        }
    }

    fun signOut() {
        screenModelScope.launch {
            try {
                authRepository.signOut()
                _state.update { it.copy(isSignedOut = true) }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to sign out") }
            }
        }
    }
}
